import { parseCsvRecords } from './parseCsvRow';

const FLOOR = { year: 2025, month: 7, day: 1 };

const DATE_COLUMN = 'submission submitted at';

const SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;

type CalendarDate = { year: number; month: number; day: number };

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function resolveSlashParts(a: number, b: number, dayFirst: boolean): { month: number; day: number } {
  if (a > 12) return { month: b, day: a };
  if (b > 12) return { month: a, day: b };
  return dayFirst ? { month: b, day: a } : { month: a, day: b };
}

export function parseSubmissionDate(
  raw: string,
  opts?: { dayFirst?: boolean },
): CalendarDate | null {
  const value = raw.trim();
  if (value === '') return null;

  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };
  }

  const slash = value.match(SLASH_DATE);
  if (slash) {
    const { month, day } = resolveSlashParts(
      Number(slash[1]),
      Number(slash[2]),
      opts?.dayFirst ?? false,
    );
    return { year: Number(slash[3]), month, day };
  }

  return null;
}

export function detectDayFirst(values: string[]): boolean {
  let dayFirstEvidence = 0;
  let monthFirstEvidence = 0;
  for (const value of values) {
    const slash = value.trim().match(SLASH_DATE);
    if (!slash) continue;
    if (Number(slash[1]) > 12) dayFirstEvidence++;
    else if (Number(slash[2]) > 12) monthFirstEvidence++;
  }
  return dayFirstEvidence >= monthFirstEvidence;
}

function isOnOrAfterFloor(date: CalendarDate): boolean {
  if (date.year !== FLOOR.year) return date.year > FLOOR.year;
  if (date.month !== FLOOR.month) return date.month > FLOOR.month;
  return date.day >= FLOOR.day;
}

function isoDate(date: CalendarDate): string {
  const mm = String(date.month).padStart(2, '0');
  const dd = String(date.day).padStart(2, '0');
  return `${date.year}-${mm}-${dd}`;
}

function serializeField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function serializeCsv(records: string[][]): string {
  return records.map((row) => row.map(serializeField).join(',')).join('\n');
}

export function applyFlashlightFloor(csvText: string): string {
  const records = parseCsvRecords(csvText);
  if (records.length < 2) return csvText;

  const headers = (records[0] ?? []).map(normalize);
  const dateIdx = headers.indexOf(DATE_COLUMN);
  if (dateIdx < 0) return csvText;

  const dayFirst = detectDayFirst(records.slice(1).map((row) => row[dateIdx] ?? ''));

  const kept: string[][] = [records[0] ?? []];
  for (let i = 1; i < records.length; i++) {
    const row = records[i] ?? [];
    const raw = row[dateIdx] ?? '';
    const date = parseSubmissionDate(raw, { dayFirst });
    if (date != null && !isOnOrAfterFloor(date)) continue;
    if (date != null && SLASH_DATE.test(raw.trim())) {
      row[dateIdx] = isoDate(date);
    }
    kept.push(row);
  }

  return serializeCsv(kept);
}
